import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import SettingsSubscriptionSection from './settings/components/SettingsSubscriptionSection';
import SettingsAccountSection from './settings/components/SettingsAccountSection';
import SettingsAppearanceSection from './settings/components/SettingsAppearanceSection';
import SettingsPrivacySection from './settings/components/SettingsPrivacySection';
import SettingsSecurityBillingSection from './settings/components/SettingsSecurityBillingSection';
import SettingsEnterpriseSection from './settings/components/SettingsEnterpriseSection';
import SettingsImageEditorModal from './settings/components/SettingsImageEditorModal';
import SettingsUpgradeModal from './settings/components/SettingsUpgradeModal';
import {useHeaderAppearanceSettings} from './settings/hooks/useHeaderAppearanceSettings';
import {RootState} from '../../store';
import {authApi} from '../../services/authApi';
import {billingApi, BillingSubscriptionSummary} from '../../services/billingApi';
import {setUser} from '../../store/slices/userSlice';
import {redirectToUrl} from '../../utils/navigation';
import styles from './settings/Settings.module.css';

const BILLING_POLL_DELAYS_MS = [0, 1500, 3000, 5000];

const wait = (durationMs: number) => new Promise((resolve) => {
  window.setTimeout(resolve, durationMs);
});

const Settings: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state: RootState) => state.auth.token);
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [subscriptionSummary, setSubscriptionSummary] = useState<BillingSubscriptionSummary | null>(null);
  const currentPlan = subscriptionSummary?.planTier || currentUser?.planTier || 'free';
  const [billingCheckoutLoading, setBillingCheckoutLoading] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingCheckoutError, setBillingCheckoutError] = useState<string | null>(null);
  const [billingStatusMessage, setBillingStatusMessage] = useState<{
    tone: 'success' | 'info' | 'error';
    text: string;
  } | null>(null);
  const {
    headerTheme,
    customHeaderImage,
    imagePreview,
    showImageEditor,
    imagePosition,
    imageZoom,
    loading,
    saving,
    error,
    setShowImageEditor,
    setImagePosition,
    setImageZoom,
    handleThemeChange,
    handleImageUpload,
    handleSaveImageSettings,
    handleCancelImageEdit,
    handleRemoveCustomImage,
  } = useHeaderAppearanceSettings(token);

  const replaceSearchParams = React.useCallback((searchParams: URLSearchParams) => {
    const nextSearch = searchParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : '',
    }, {replace: true});
  }, [location.pathname, navigate]);

  const refreshBillingState = React.useCallback(async () => {
    if (!token) {
      setSubscriptionSummary(null);
      return null;
    }

    const [refreshedUser, refreshedSubscription] = await Promise.all([
      authApi.getCurrentUser(token),
      billingApi.getSubscriptionSummary(token),
    ]);

    dispatch(setUser(refreshedUser));
    setSubscriptionSummary(refreshedSubscription);

    return {
      refreshedUser,
      refreshedSubscription,
    };
  }, [dispatch, token]);

  const handleUpgrade = () => {
    setBillingCheckoutError(null);
    setShowUpgradeModal(true);
  };

  const handleSubscribe = async () => {
    if (!token) {
      setBillingCheckoutError('You must be signed in to upgrade to XPSR Pro.');
      return;
    }

    let didRedirect = false;
    setBillingCheckoutLoading(true);
    setBillingCheckoutError(null);

    try {
      const session = await billingApi.createCheckoutSession(token);
      didRedirect = true;
      redirectToUrl(session.url);
    } catch (billingError) {
      setBillingCheckoutError(
        billingError instanceof Error ? billingError.message : 'Failed to start Stripe checkout.'
      );
    } finally {
      if (!didRedirect) {
        setBillingCheckoutLoading(false);
      }
    }
  };

  const handleManageSubscription = async () => {
    if (!token) {
      setBillingCheckoutError('You must be signed in to manage your subscription.');
      return;
    }

    let didRedirect = false;
    setBillingPortalLoading(true);
    setBillingCheckoutError(null);

    try {
      const portalUrl = await billingApi.createPortalSession(token);
      didRedirect = true;
      redirectToUrl(portalUrl);
    } catch (billingError) {
      setBillingCheckoutError(
        billingError instanceof Error ? billingError.message : 'Failed to open Stripe billing portal.'
      );
    } finally {
      if (!didRedirect) {
        setBillingPortalLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!token) {
      setSubscriptionSummary(null);
      return;
    }

    let isMounted = true;

    const loadBillingSummary = async () => {
      try {
        const nextSummary = await billingApi.getSubscriptionSummary(token);

        if (!isMounted) {
          return;
        }

        setSubscriptionSummary(nextSummary);
      } catch (billingError) {
        if (!isMounted) {
          return;
        }

        setBillingStatusMessage((currentMessage) => currentMessage || {
          tone: 'error',
          text: billingError instanceof Error ? billingError.message : 'Failed to load billing status.',
        });
      }
    };

    void loadBillingSummary();

    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get('upgrade') !== '1') {
      return;
    }

    setShowUpgradeModal(true);
    searchParams.delete('upgrade');
    replaceSearchParams(searchParams);
  }, [location.search, replaceSearchParams]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const billingStatus = searchParams.get('billing');

    if (!billingStatus) {
      return;
    }

    if (billingStatus === 'cancel') {
      setBillingStatusMessage({
        tone: 'info',
        text: 'Stripe checkout was canceled. Your plan has not changed.',
      });
      searchParams.delete('billing');
      searchParams.delete('session_id');
      replaceSearchParams(searchParams);
      return;
    }

    if (!token || billingStatus !== 'success') {
      return;
    }

    const sessionId = searchParams.get('session_id');
    let isActive = true;

    const verifyAndRefresh = async () => {
      try {
        setBillingStatusMessage({
          tone: 'info',
          text: 'We’re finalizing your Pro upgrade. This can take a few seconds.',
        });

        if (sessionId) {
          await billingApi.verifyCheckoutSession(token, sessionId);
        }

        for (const delayMs of BILLING_POLL_DELAYS_MS) {
          if (delayMs > 0) {
            await wait(delayMs);
          }

          if (!isActive) {
            return;
          }

          const refreshedState = await refreshBillingState();

          if (!isActive || !refreshedState) {
            return;
          }

          if (refreshedState.refreshedSubscription.planTier === 'pro') {
            setBillingStatusMessage({
              tone: 'success',
              text: 'Your XPSR Pro upgrade is active.',
            });
            setShowUpgradeModal(false);
            setBillingCheckoutError(null);

            searchParams.delete('billing');
            searchParams.delete('session_id');
            replaceSearchParams(searchParams);
            return;
          }
        }

        if (!isActive) {
          return;
        }

        setBillingStatusMessage({
          tone: 'info',
          text: 'We’re still waiting for Stripe to confirm your Pro upgrade. Refresh this page in a moment if your plan has not updated yet.',
        });
      } catch (refreshError) {
        if (!isActive) {
          return;
        }

        setBillingStatusMessage({
          tone: 'error',
          text: refreshError instanceof Error
            ? refreshError.message
            : 'We could not refresh your billing status yet. Please refresh this page in a moment.',
        });
      } finally {
        if (isActive) {
          setBillingCheckoutLoading(false);
        }
      }
    };

    void verifyAndRefresh();

    return () => {
      isActive = false;
    };
  }, [location.search, refreshBillingState, replaceSearchParams, token]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get('enterprise') !== 'joined') {
      return;
    }

    setBillingStatusMessage({
      tone: 'success',
      text: currentUser?.enterprise
        ? `Your ${currentUser.enterprise.organizationName} Enterprise seat is active.`
        : 'Your Enterprise seat is active.',
    });

    searchParams.delete('enterprise');
    replaceSearchParams(searchParams);
  }, [currentUser?.enterprise, location.search, replaceSearchParams]);

  return (
    <div className={styles.pageRoot}>
      <SettingsSubscriptionSection
        currentPlan={currentPlan}
        subscriptionSummary={subscriptionSummary}
        manageLoading={billingPortalLoading}
        onUpgrade={handleUpgrade}
        onManageSubscription={handleManageSubscription}
      />

      <div className={styles.settingsContent}>
        <div className={styles.settingsSectionShell}>
        {billingStatusMessage ? (
          <div
            className={`${styles.billingStatusMessage} ${
              billingStatusMessage.tone === 'success'
                ? styles.billingStatusMessageSuccess
                : billingStatusMessage.tone === 'error'
                  ? styles.billingStatusMessageError
                  : styles.billingStatusMessageInfo
            }`.trim()}
          >
            {billingStatusMessage.text}
          </div>
        ) : null}

        <SettingsEnterpriseSection
          token={token}
          currentUser={currentUser}
        />

        <div className={styles.settingsPrimaryGrid}>
          <SettingsAccountSection />

          <SettingsAppearanceSection
            headerTheme={headerTheme}
            imagePreview={imagePreview}
            imagePosition={imagePosition}
            imageZoom={imageZoom}
            loading={loading}
            saving={saving}
            error={error}
            onImageUpload={handleImageUpload}
            onOpenImageEditor={() => setShowImageEditor(true)}
            onRemoveCustomImage={handleRemoveCustomImage}
            onThemeChange={handleThemeChange}
          />
        </div>

        <div className={styles.settingsSecondaryGrid}>
          <SettingsPrivacySection />
          <SettingsSecurityBillingSection
            currentPlan={currentPlan}
            manageLoading={billingPortalLoading}
            managementAvailable={Boolean(subscriptionSummary?.managementUrlAvailable)}
            onManageSubscription={handleManageSubscription}
          />
        </div>

        </div>
      </div>

      {showImageEditor && customHeaderImage ? (
        <SettingsImageEditorModal
          customHeaderImage={customHeaderImage}
          imagePosition={imagePosition}
          imageZoom={imageZoom}
          error={error}
          isSaving={saving}
          onCancel={handleCancelImageEdit}
          onSave={handleSaveImageSettings}
          onChangeImagePosition={setImagePosition}
          onChangeImageZoom={setImageZoom}
        />
      ) : null}

      {showUpgradeModal ? (
        <SettingsUpgradeModal
          onClose={() => setShowUpgradeModal(false)}
          onStartTrial={handleSubscribe}
          loading={billingCheckoutLoading}
          error={billingCheckoutError}
        />
      ) : null}
    </div>
  );
};

export default Settings;
